import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { ChevronDown, ChevronUp, Languages, Loader2, RefreshCcw, Sparkles } from 'lucide-react'
import { Card, CardContent } from './ui/card'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { useTextTranslation } from '../hooks/useTextTranslation'
import { cn } from '../lib/utils'

// Cheap script detection — anything with Arabic-block characters
// gets treated as Arabic; everything else as English. Good enough
// for "flip to the other language" semantics.
function isArabicText(s) {
  return /[؀-ۿ]/.test(String(s || ''))
}

// AI-generated weekly review of the user's own data. Reads from
// weekly_insights (one row per user per week, populated by the
// Sunday cron + on-demand refresh). Hides when no row exists
// — the user just sees nothing until either the cron runs or
// they tap a refresh-style action; no half-empty card.
//
// Collapsed by default to ~5 lines (line-clamp). Tap to expand.
// Refresh button calls the weekly-insights edge function with
// force=true for the current user, then re-reads the row.
export default function WeeklyInsightsCard() {
  const { t, i18n } = useTranslation()
  const { user } = useAuth()
  const isAr = i18n.language === 'ar'
  const [row, setRow] = useState(null)
  const [loading, setLoading] = useState(true)
  const [expanded, setExpanded] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState(null)
  // Translation toggle — flips the content into the opposite
  // language regardless of UI locale. Same MS-Translator-backed
  // pattern as the news digest / AI summary.
  const translator = useTextTranslation()
  const [showTranslated, setShowTranslated] = useState(false)

  const handleTranslate = useCallback(async () => {
    if (!row?.content || translator.loading) return
    if (translator.translation) {
      setShowTranslated((v) => !v)
      return
    }
    const sourceIsArabic = isArabicText(row.content)
    const target = sourceIsArabic ? 'en' : 'ar'
    const out = await translator.translate(row.content, target)
    if (out) setShowTranslated(true)
  }, [row?.content, translator])

  // Reset translation state whenever the underlying row changes —
  // e.g. after a regenerate refetch fills a fresh week's content.
  // `translator.reset` is wrapped in useCallback (empty deps) inside
  // useTextTranslation so its identity is stable across renders.
  const { reset: resetTranslation, translate } = translator
  useEffect(() => {
    setShowTranslated(false)
    resetTranslation()
  }, [row?.week_start_date, resetTranslation])

  // Auto-translate when the row's source language doesn't match the
  // user's UI locale — so an Arabic user landing on a Sunday-cron row
  // that came back in English doesn't see a wall of English text until
  // they remember to tap the Languages button. Mirrors the auto-flip
  // the news DigestCard does. The manual button still works for
  // toggling back to the original / flipping to the opposite language.
  const currentLocale = isAr ? 'ar' : 'en'
  const sourceLang = row?.content && isArabicText(row.content) ? 'ar' : 'en'
  const translateNeeded = !!row?.content && sourceLang !== currentLocale
  useEffect(() => {
    if (!translateNeeded) return undefined
    let alive = true
    translate(row.content, currentLocale).then((out) => {
      if (alive && out) setShowTranslated(true)
    })
    return () => { alive = false }
  }, [row?.content, currentLocale, translateNeeded, translate])

  const fetchInsights = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }
    setLoading(true)
    const weekStart = mondayOf(new Date()).toISOString().slice(0, 10)
    // RLS scopes to user; we still pass the week explicitly so a
    // refresh against the dashboard while the cron is mid-week
    // doesn't surprise the user with a stale row.
    const { data } = await supabase
      .from('weekly_insights')
      .select('content, provider, stats, generated_at, week_start_date')
      .eq('week_start_date', weekStart)
      .maybeSingle()
    setRow(data || null)
    setLoading(false)
  }, [user])

  useEffect(() => { fetchInsights() }, [fetchInsights])

  const handleRefresh = useCallback(async () => {
    if (refreshing) return
    setRefreshing(true)
    setRefreshError(null)
    try {
      const { data, error } = await supabase.functions.invoke('weekly-insights', {
        body: { force: true, language: isAr ? 'ar' : 'en' },
      })
      if (error) throw error
      // The function returns 200 with a status even when generation
      // didn't produce a row (no_activity / llm_failed / write_failed)
      // — surface those instead of silently showing the same CTA.
      const status = data?.status
      if (status && status !== 'created' && status !== 'already_exists') {
        const key = `insights.status.${status}`
        const translated = t(key)
        throw new Error(translated === key ? (data?.error || status) : translated)
      }
      await fetchInsights()
    } catch (e) {
      setRefreshError(e?.message || 'failed')
    } finally {
      setRefreshing(false)
    }
  }, [refreshing, isAr, fetchInsights, t])

  // Hide entirely while loading the initial fetch — there's no
  // skeleton because the card may genuinely not exist yet for a new
  // user, and a permanent skeleton on the dashboard would be noisy.
  if (loading) return null
  // Show the empty-state CTA when no row exists yet — gives the user
  // a way to generate one without waiting until Sunday's cron tick.
  if (!row) {
    return (
      <Card className="border-app-primary-500/30 bg-app-primary-500/5 overflow-hidden">
        <CardContent className="p-3 sm:p-4 flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500" dir="auto">
              {t('insights.title')}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5" dir="auto">
              {t('insights.emptyHint')}
            </p>
            {/* This branch previously swallowed refresh errors — the
                spinner stopped and nothing changed on screen. */}
            {refreshError && (
              <p className="text-[11px] text-red-600 mt-1" dir="auto">{refreshError}</p>
            )}
          </div>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-xs font-semibold text-app-primary-500 hover:underline inline-flex items-center gap-1 disabled:opacity-60"
            dir="auto"
          >
            {refreshing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Sparkles className="w-3.5 h-3.5" />
            )}
            {t('insights.generateNow')}
          </button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="border-app-primary-500/30 bg-app-primary-500/5 overflow-hidden">
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="w-4 h-4 text-app-primary-500 flex-shrink-0" />
          <p
            className="text-[11px] font-semibold uppercase tracking-wide text-app-primary-500 flex-1 min-w-0"
            dir="auto"
          >
            {t('insights.title')}
          </p>
          <button
            type="button"
            onClick={handleTranslate}
            disabled={translator.loading}
            aria-label={t(showTranslated ? 'insights.showOriginal' : 'insights.translate')}
            title={t(showTranslated ? 'insights.showOriginal' : 'insights.translate')}
            aria-pressed={showTranslated}
            className={cn(
              'p-1 rounded-md hover:bg-accent transition-colors',
              showTranslated ? 'text-app-primary-500' : 'text-muted-foreground hover:text-app-primary-500',
              translator.loading && 'opacity-60',
            )}
          >
            {translator.loading
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Languages className="w-3.5 h-3.5" />}
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label={t('insights.refresh')}
            title={t('insights.refresh')}
            className="p-1 rounded-md hover:bg-accent transition-colors text-muted-foreground hover:text-app-primary-500 disabled:opacity-60"
          >
            <RefreshCcw className={cn('w-3.5 h-3.5', refreshing && 'animate-spin')} />
          </button>
        </div>
        {refreshError && (
          <p className="text-[11px] text-red-600 mb-2" dir="auto">{refreshError}</p>
        )}
        {translator.error && (
          <p className="text-[11px] text-red-600 mb-2" dir="ltr">{translator.error}</p>
        )}
        {(() => {
          const displayed = showTranslated && translator.translation
            ? translator.translation
            : row.content
          const displayedDir = showTranslated && translator.translation
            ? (isArabicText(translator.translation) ? 'rtl' : 'ltr')
            : (isArabicText(row.content) ? 'rtl' : (isAr ? 'rtl' : 'ltr'))
          return (
            <div
              className={cn(
                'prose prose-sm dark:prose-invert max-w-none text-foreground leading-relaxed',
                !expanded && 'line-clamp-5',
              )}
              dir={displayedDir}
            >
              <ReactMarkdown>{displayed}</ReactMarkdown>
            </div>
          )
        })()}
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 flex items-center gap-1 text-[11px] text-app-primary-500 hover:underline"
          dir="auto"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3.5 h-3.5" />
              {t('insights.collapse')}
            </>
          ) : (
            <>
              <ChevronDown className="w-3.5 h-3.5" />
              {t('insights.expand')}
            </>
          )}
        </button>
      </CardContent>
    </Card>
  )
}

function mondayOf(d) {
  const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const day = utc.getUTCDay()
  const offset = (day + 6) % 7
  utc.setUTCDate(utc.getUTCDate() - offset)
  return utc
}
